import {
  Award,
  Clock,
  Cpu,
  Gift,
  Package,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./homeTheme";
import { FortAgentSkuConfigItem } from "@/api/types";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS } from "@/config/env";

export function AgentDetailDialog({
  agent,
  open,
  onClose,
}: {
  agent: FortAgentSkuConfigItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!agent) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu size={16} style={{ color: C.primary }} />
            <span style={{ color: C.primary }}>
              {agent.name}（{agent.name}）
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div
            className="rounded-lg p-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div className="flex justify-between text-sm">
              <span style={{ color: C.muted }}>
                {t("homePage.agentDetailDialog.basePrice")}
              </span>
              <span className="mono-num font-bold">
                {formatUsdPrefix(
                  formatThousands(formatEtherWithDecimals(agent.basePriceUsdt))
                )}
              </span>
            </div>
            {agent.sellPriceUsdt !== agent.basePriceUsdt && (
              <div className="flex justify-between text-sm mt-2">
                <span style={{ color: C.muted }}>
                  {t("homePage.agentDetailDialog.currentPrice")}
                </span>
                <span className="mono-num font-bold" style={{ color: C.amber }}>
                  {formatUsdPrefix(
                    formatThousands(
                      formatEtherWithDecimals(agent.sellPriceUsdt)
                    )
                  )}
                </span>
              </div>
            )}
          </div>

          <div
            className="text-[10px] font-semibold mb-1"
            style={{ color: C.muted }}
          >
            {t("homePage.agentDetailDialog.benefitsTitle")}
          </div>
          {[
            {
              icon: <TrendingUp size={12} />,
              title: t("homePage.agentDetailDialog.custodyBoostTitle", {
                value: agent.aiManageAddSpeed / 100,
              }),
              desc: t("homePage.agentDetailDialog.custodyBoostDesc"),
              color: C.primary,
            },
            {
              icon: <Zap size={12} />,
              title: t("homePage.agentDetailDialog.releaseTitle", {
                value: agent.exitMultiple,
              }),
              desc: t("homePage.agentDetailDialog.releaseDesc", {
                multiple: agent.exitMultiple / 100,
                price: formatUsdPrefix(
                  formatThousands(formatEtherWithDecimals(agent.basePriceUsdt))
                ),
              }),
              color: C.green,
            },
            {
              icon: <Gift size={12} />,
              title: t("homePage.agentDetailDialog.aifTokenTitle", {
                value: formatUsdPrefix(
                  formatThousands(formatEtherWithDecimals(agent.aifValueUsdt))
                ),
                tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
              }),
              desc: t("homePage.agentDetailDialog.aifTokenDesc", {
                days: agent.aifDistributeDays,
                dailyValue: formatUsdPrefix(
                  formatThousands(
                    formatEtherWithDecimals(
                      +agent.aifValueUsdt / agent.aifDistributeDays
                    )
                  )
                ),
                tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
              }),
              color: C.amber,
            },
            // {
            //   icon: <Award size={12} />,
            //   title: t("homePage.agentDetailDialog.teamLevelTitle", {
            //     level: agent.giveLevel,
            //   }),
            //   desc: t("homePage.agentDetailDialog.teamLevelDesc", {
            //     days: agent.giveLevelDurationDays,
            //   }),
            //   color: C.blue,
            // },
          ].map(item => (
            <div
              key={item.title}
              className="flex items-start gap-2 p-2.5 rounded-lg"
              style={{ background: "rgba(255,255,255,0.02)" }}
            >
              <div
                className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: `${item.color}10` }}
              >
                <span style={{ color: item.color }}>{item.icon}</span>
              </div>
              <div>
                <div className="text-[11px] font-semibold">{item.title}</div>
                <div
                  className="text-[9px] leading-relaxed mt-0.5"
                  style={{ color: C.dim }}
                >
                  {item.desc}
                </div>
              </div>
            </div>
          ))}

          <div className="text-[10px] space-y-1 pt-1" style={{ color: C.dim }}>
            <div className="flex items-center gap-1">
              <Package size={9} />
              {t("homePage.agentDetailDialog.hardwareNote")}
            </div>
            <div className="flex items-center gap-1">
              <Clock size={9} />
              {t("homePage.agentDetailDialog.priceNote")}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
